import * as Sentry from '@sentry/nextjs';
import {
  type PrecomputedEvaluationsInterface,
  useStatsigClient,
} from '@statsig/react-bindings';
import { isbot } from 'isbot';
import { useRouter } from 'next/compat/router';
import { useCallback } from 'react';

import { useMarket } from '../providers/MarketProvider';
import { useSembonusUser } from '../providers/UserProvider';
import { AffiliateService } from '../services/AffiliateService/AffiliateService';
import {
  flattenPropertiesAsStrings,
  isSSR,
  propertiesAsStrings,
  removeNestedPropertyFromObject,
} from '../utils/helpers';
import { getUnlocalizedUrl } from '../utils/markets';
import type { AllowedEventNames } from '../utils/types';

export const useLogService = () => {
  const statsigClient = useStatsigClient();
  const user = useSembonusUser();
  const market = useMarket();
  const userAgent = (!isSSR() && navigator.userAgent) || '';
  const isCrawler = isbot(userAgent);
  const router = useRouter();

  const enrichMetadata = useCallback(
    (
      inputMetadata?:
        | Record<string, string | null | undefined>
        | null
        | undefined
    ) => {
      let metadata = {
        ...(!isSSR()
          ? {
              origin: window.location.origin,
              pathname: window.location.pathname,
              search: window.location.search,
              href: window.location.href,
              hash: window.location.hash,
              host: window.location.host,
              hostname: window.location.hostname,
              referrer: document.referrer,
              isCrawler: `${isCrawler}`,
              route: router?.route ?? '',
              userAgent,
            }
          : {}),
        ...propertiesAsStrings(market),
        ...flattenPropertiesAsStrings({
          user: removeNestedPropertyFromObject(user, 'memberOptions'),
        }),
        ...propertiesAsStrings(inputMetadata),
      };

      const unlocalized =
        market && !isSSR()
          ? getUnlocalizedUrl(market, window.location.pathname)
          : undefined;

      if (unlocalized) {
        metadata = {
          ...metadata,
          ...flattenPropertiesAsStrings({ unlocalized }),
        };
      }

      const affiliate = { affiliateId: AffiliateService.getAffiliateId() };
      if (affiliate.affiliateId) {
        metadata = {
          ...metadata,
          ...flattenPropertiesAsStrings({ affiliate }),
        };
      }

      return metadata;
    },
    [market, user]
  );

  const logEvent = (
    event: AllowedEventNames,
    value?: Parameters<PrecomputedEvaluationsInterface['logEvent']>[1],
    inputMetadata?: Record<string, string | null | undefined> | null | undefined
  ) => {
    const metadata = enrichMetadata(inputMetadata);

    if (!isCrawler) {
      statsigClient.logEvent(event, value, metadata);

      const logParams = new URLSearchParams(window.location.search).getAll(
        'log'
      );
      if (logParams.includes('Statsig')) {
        // eslint-disable-next-line no-console
        console.log('Statsig', 'Event', { event, value, metadata });
      }
    }

    // just to enforce we don't accidentally change the event properties
    // that need to be in the dataLayer
    (
      window.dataLayer as Array<{
        event: string;
        metadata: Record<string, unknown>;
      }>
    )?.push({ event, metadata });
  };

  const logErrorWithMetadata = (
    inputMetadata: Record<string, string | null | undefined> | null | undefined,
    ...args: Parameters<typeof Sentry.captureException>
  ) => {
    const metadata = enrichMetadata(inputMetadata);

    return Sentry.withScope((scope) => {
      scope.setTags(metadata);

      return Sentry.captureException(...args);
    });
  };

  return {
    logEvent,
    logError: (...args: Parameters<typeof Sentry.captureException>) =>
      logErrorWithMetadata(null, ...args),
    logErrorWithMetadata,
  };
};
